/**
 * @lodex-pro/web-portal/routes/PortalRoutes — the company's routes (OPP PR 7, 0214).
 *
 * OPERATOR_ADMIN and FOREMAN, under OP_ROUTES_PUMPERS. A route is a set of wells (their tanks
 * come with them) plus battery tanks (tanks on no single well), and the foremen who run it.
 *
 *   - The list: name, wells, tanks, foremen, active state; create, rename, deactivate.
 *   - Editing one route: a searchable well picker (SWD and INJ alike tagged "Disposal well"),
 *     the battery tanks, both saved as the full set; and its foremen. An admin adds or removes
 *     any of the company's foremen; a foreman adds or removes only themself, by /me's userId
 *     (the same lodeX user id the API checks the request against).
 *
 * A pumper gets no page (they go Home) and no sidebar entry. The API refuses them too
 * (MANAGE_ROUTES); this is the UX, not the gate.
 */

import React from 'react';
import { Navigate } from 'react-router-dom';
import type {
  CompanyForemanView,
  CompanyWellView,
  OperatorTankView,
  RouteView,
} from '@lodex-pro/shared/validation/operator-portal-routes';
import { usePortalMe } from '../hooks/useOperatorPortal';
import {
  useCompanyForemen,
  useCompanyWells,
  useCreateRoute,
  useOperatorTanks,
  usePortalRoutes,
  useSetRouteForeman,
  useSetRouteTanks,
  useSetRouteWells,
  useUpdateRoute,
} from '../hooks/useTankSetupAndRoutes';
import { PortalDataError } from '../components/PortalDataError';
import { portalErrorText } from '../components/portal-error-text';
import { portalIsAdmin, portalLacks, portalManagesRoutes } from '../nav/portal-features';
import { PremiumStub } from './PremiumStub';
import {
  BUTTON,
  CARD,
  INPUT,
  MUTED,
  PRIMARY_BUTTON,
  TAG,
  localDateTime,
  plural,
} from './operator-tank-labels';
import { formatNumber } from '@lodex-pro/shared/number-format';

/** The most wells the picker lists at once; a search narrows the rest. */
const WELL_LIST_LIMIT = 100;

const TH: React.CSSProperties = { textAlign: 'left', padding: '8px 12px' };
const TH_R: React.CSSProperties = { textAlign: 'right', padding: '8px 12px' };
const TD: React.CSSProperties = { padding: '8px 12px', verticalAlign: 'top' };
const TD_R: React.CSSProperties = { ...TD, textAlign: 'right' };
const ERROR_TEXT: React.CSSProperties = { color: 'var(--color-danger)', fontSize: '13px' };
const OK_TEXT: React.CSSProperties = { color: 'var(--color-success)', fontSize: '13px' };

interface Viewer {
  readonly userId: string;
  readonly isAdmin: boolean;
}

export function PortalRoutes(): React.JSX.Element {
  const me = usePortalMe();
  if (me.data === undefined) {
    return (
      <main style={{ padding: '24px' }}>
        {me.isError ? <p role="alert">Could not load routes.</p> : <p role="status">Loading…</p>}
      </main>
    );
  }
  if (!portalManagesRoutes(me.data)) return <Navigate to="/portal" replace />;
  if (portalLacks(me.data, 'OP_ROUTES_PUMPERS')) return <PremiumStub feature="routes" />;
  return <RoutesBody viewer={{ userId: me.data.userId, isAdmin: portalIsAdmin(me.data) }} />;
}

function foremenText(r: RouteView): string {
  if (r.foremen.length === 0) return 'None';
  return r.foremen.map((f) => f.name ?? 'Unnamed foreman').join(', ');
}

function RoutesBody({ viewer }: { readonly viewer: Viewer }): React.JSX.Element {
  const routes = usePortalRoutes();
  const [editingId, setEditingId] = React.useState<string | null>(null);
  const list = [...(routes.data?.routes ?? [])].sort(
    (a, b) => Number(b.isActive) - Number(a.isActive) || a.name.localeCompare(b.name),
  );
  const editing = list.find((r) => r.id === editingId) ?? null;

  return (
    <main style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
      <h1 style={{ marginBottom: '4px' }}>Routes</h1>
      <p style={{ ...MUTED, fontSize: '13px', marginTop: 0, marginBottom: '16px' }}>
        A route is a set of wells, with their tanks, plus any battery tanks, and the foremen who run
        it.
      </p>

      <CreateRoute />

      {routes.isLoading && <p role="status">Loading routes…</p>}
      {routes.isError && <PortalDataError error={routes.error} fallback="Could not load routes." />}

      {routes.data !== undefined && (
        <table
          aria-label="Routes"
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            background: 'var(--color-bg-secondary)',
            border: '1px solid var(--color-border-primary)',
            marginBottom: '20px',
          }}
        >
          <thead>
            <tr style={{ background: 'var(--color-bg-tertiary)' }}>
              <th style={TH}>Route</th>
              <th style={TH_R}>Wells</th>
              <th style={TH_R}>Tanks</th>
              <th style={TH}>Foremen</th>
              <th style={TH}>Status</th>
              <th style={TH}>Last changed</th>
              <th style={TH} aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {list.length === 0 ? (
              <tr>
                <td colSpan={7} style={{ padding: '16px', textAlign: 'center', ...MUTED }}>
                  No routes yet. Create one above.
                </td>
              </tr>
            ) : (
              list.map((r) => (
                <RouteRow
                  key={r.id}
                  route={r}
                  editing={r.id === editingId}
                  onEdit={(): void => {
                    setEditingId(r.id === editingId ? null : r.id);
                  }}
                />
              ))
            )}
          </tbody>
        </table>
      )}

      {editing !== null && (
        <RouteEditor
          key={editing.id}
          route={editing}
          viewer={viewer}
          onClose={(): void => {
            setEditingId(null);
          }}
        />
      )}
    </main>
  );
}

function CreateRoute(): React.JSX.Element {
  const create = useCreateRoute();
  const [name, setName] = React.useState('');
  const [message, setMessage] = React.useState<{ ok: boolean; text: string } | null>(null);

  const submit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    const trimmed = name.trim();
    if (trimmed === '') {
      setMessage({ ok: false, text: 'Give the route a name.' });
      return;
    }
    setMessage(null);
    try {
      await create.mutateAsync({ name: trimmed });
      setName('');
      setMessage({ ok: true, text: `Route ${trimmed} created.` });
    } catch (err) {
      setMessage({ ok: false, text: portalErrorText(err, 'Could not create that route.') });
    }
  };

  return (
    <form
      aria-label="Create a route"
      onSubmit={(e): void => {
        void submit(e);
      }}
      style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '16px' }}
    >
      <input
        aria-label="New route name"
        placeholder="New route name"
        value={name}
        maxLength={120}
        onChange={(e): void => {
          setName(e.target.value);
        }}
        style={{ ...INPUT, width: '260px' }}
      />
      <button type="submit" style={PRIMARY_BUTTON} disabled={create.isPending}>
        Create route
      </button>
      {message !== null && (
        <span role={message.ok ? 'status' : 'alert'} style={message.ok ? OK_TEXT : ERROR_TEXT}>
          {message.text}
        </span>
      )}
    </form>
  );
}

function RouteRow({
  route: r,
  editing,
  onEdit,
}: {
  readonly route: RouteView;
  readonly editing: boolean;
  readonly onEdit: () => void;
}): React.JSX.Element {
  const update = useUpdateRoute();
  const [renaming, setRenaming] = React.useState(false);
  const [name, setName] = React.useState(r.name);
  const [error, setError] = React.useState<string | null>(null);

  const save = async (nextName: string, isActive: boolean): Promise<boolean> => {
    setError(null);
    try {
      await update.mutateAsync({ id: r.id, name: nextName, isActive });
      return true;
    } catch (err) {
      setError(portalErrorText(err, 'Could not save that route.'));
      return false;
    }
  };

  const rename = async (): Promise<void> => {
    const trimmed = name.trim();
    if (trimmed === '') {
      setError('Give the route a name.');
      return;
    }
    if (await save(trimmed, r.isActive)) setRenaming(false);
  };

  return (
    <tr
      aria-label={`Route ${r.name}`}
      style={{ borderTop: '1px solid var(--color-border-primary)' }}
    >
      <td style={TD}>
        {renaming ? (
          <span style={{ display: 'flex', gap: '6px' }}>
            <input
              aria-label="Route name"
              value={name}
              maxLength={120}
              onChange={(e): void => {
                setName(e.target.value);
              }}
              style={INPUT}
            />
            <button
              type="button"
              style={PRIMARY_BUTTON}
              disabled={update.isPending}
              onClick={(): void => {
                void rename();
              }}
            >
              Save
            </button>
            <button
              type="button"
              style={BUTTON}
              onClick={(): void => {
                setRenaming(false);
                setName(r.name);
                setError(null);
              }}
            >
              Cancel
            </button>
          </span>
        ) : (
          r.name
        )}
        {error !== null && (
          <div role="alert" style={{ ...ERROR_TEXT, marginTop: '4px' }}>
            {error}
          </div>
        )}
      </td>
      <td style={{ ...TD_R, fontVariantNumeric: 'tabular-nums' }}>{r.wells.length}</td>
      <td style={{ ...TD_R, fontVariantNumeric: 'tabular-nums' }}>{formatNumber(r.tankCount)}</td>
      <td style={TD}>{foremenText(r)}</td>
      <td style={TD}>
        <span style={TAG}>{r.isActive ? 'Active' : 'Inactive'}</span>
      </td>
      <td style={{ ...TD, ...MUTED, fontSize: '13px' }}>{localDateTime(r.updatedAt)}</td>
      <td style={TD}>
        <span style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end' }}>
          <button type="button" style={BUTTON} aria-label={`Edit ${r.name}`} onClick={onEdit}>
            {editing ? 'Close' : 'Edit'}
          </button>
          {!renaming && (
            <button
              type="button"
              style={BUTTON}
              aria-label={`Rename ${r.name}`}
              onClick={(): void => {
                setName(r.name);
                setRenaming(true);
              }}
            >
              Rename
            </button>
          )}
          <button
            type="button"
            style={BUTTON}
            aria-label={`${r.isActive ? 'Deactivate' : 'Reactivate'} ${r.name}`}
            disabled={update.isPending}
            onClick={(): void => {
              void save(r.name, !r.isActive);
            }}
          >
            {r.isActive ? 'Deactivate' : 'Reactivate'}
          </button>
        </span>
      </td>
    </tr>
  );
}

function RouteEditor({
  route,
  viewer,
  onClose,
}: {
  readonly route: RouteView;
  readonly viewer: Viewer;
  readonly onClose: () => void;
}): React.JSX.Element {
  return (
    <section aria-label={`Edit route ${route.name}`} style={{ ...CARD, padding: '16px 20px' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: '12px' }}>
        <h2 style={{ fontSize: '17px', margin: 0 }}>{route.name}</h2>
        <span style={{ ...MUTED, fontSize: '13px' }}>
          {plural(route.wells.length, 'well')} · {plural(route.tankCount, 'tank')}
        </span>
        <button type="button" style={{ ...BUTTON, marginLeft: 'auto' }} onClick={onClose}>
          Done
        </button>
      </div>
      <WellsEditor route={route} />
      <BatteryTanksEditor route={route} />
      <ForemenEditor route={route} viewer={viewer} />
    </section>
  );
}

function wellMatches(w: CompanyWellView, q: string): boolean {
  if (q === '') return true;
  const hay = [w.wellName, w.apiNumber, w.county, w.state, w.isDisposal ? 'disposal' : null]
    .filter((s): s is string => s !== null)
    .join(' ')
    .toLowerCase();
  return q
    .toLowerCase()
    .split(/\s+/)
    .every((part) => hay.includes(part));
}

function wellName(w: {
  readonly wellName: string | null;
  readonly apiNumber: string | null;
}): string {
  if (w.wellName !== null && w.wellName.trim() !== '') return w.wellName;
  return w.apiNumber === null ? 'Unnamed well' : `Well ${w.apiNumber}`;
}

function DisposalTag(): React.JSX.Element {
  return (
    <span
      style={{
        ...TAG,
        background: 'rgba(123, 150, 232, 0.18)',
        color: 'var(--color-accent-blue-dark)',
      }}
    >
      Disposal well
    </span>
  );
}

function WellsEditor({ route }: { readonly route: RouteView }): React.JSX.Element {
  const wells = useCompanyWells();
  const setWells = useSetRouteWells();
  const [selected, setSelected] = React.useState<ReadonlySet<string>>(
    () => new Set(route.wells.map((w) => w.wellId)),
  );
  const [query, setQuery] = React.useState('');
  const [message, setMessage] = React.useState<{ ok: boolean; text: string } | null>(null);

  const all = [...(wells.data?.wells ?? [])].sort((a, b) => wellName(a).localeCompare(wellName(b)));
  const matches = all.filter((w) => wellMatches(w, query.trim()));
  const shown = matches.slice(0, WELL_LIST_LIMIT);

  const toggle = (id: string): void => {
    setMessage(null);
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const save = async (): Promise<void> => {
    setMessage(null);
    try {
      await setWells.mutateAsync({ id: route.id, wellIds: [...selected] });
      setMessage({ ok: true, text: 'Wells saved.' });
    } catch (err) {
      setMessage({ ok: false, text: portalErrorText(err, 'Could not save the wells.') });
    }
  };

  return (
    <section aria-label="Wells on this route" style={{ marginTop: '16px' }}>
      <h3 style={{ fontSize: '15px', margin: '0 0 6px' }}>Wells</h3>
      <p style={{ ...MUTED, fontSize: '13px', margin: '0 0 8px' }}>
        {plural(selected.size, 'well')} chosen. Each well&rsquo;s tanks come with it.
      </p>
      <input
        aria-label="Search wells"
        placeholder="Search by name, API number or county"
        value={query}
        onChange={(e): void => {
          setQuery(e.target.value);
        }}
        style={{ ...INPUT, width: '320px', marginBottom: '8px' }}
      />
      {wells.isLoading && <p role="status">Loading wells…</p>}
      {wells.isError && <PortalDataError error={wells.error} fallback="Could not load wells." />}
      {wells.data !== undefined && (
        <>
          {shown.length === 0 ? (
            <p style={{ ...MUTED, fontSize: '13px' }}>
              {all.length === 0 ? 'Your company has no wells yet.' : 'No wells match that search.'}
            </p>
          ) : (
            <ul
              style={{
                listStyle: 'none',
                margin: 0,
                padding: 0,
                maxHeight: '320px',
                overflowY: 'auto',
                border: '1px solid var(--color-border-primary)',
                borderRadius: '12px',
              }}
            >
              {shown.map((w) => (
                <li
                  key={w.wellId}
                  style={{
                    padding: '6px 10px',
                    borderTop: '1px solid var(--color-border-primary)',
                  }}
                >
                  <label style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                    <input
                      type="checkbox"
                      checked={selected.has(w.wellId)}
                      onChange={(): void => {
                        toggle(w.wellId);
                      }}
                    />
                    <span>{wellName(w)}</span>
                    {w.apiNumber !== null && (
                      <span style={{ ...MUTED, fontSize: '12px' }}>API {w.apiNumber}</span>
                    )}
                    {w.isDisposal && <DisposalTag />}
                    <span style={{ ...MUTED, fontSize: '12px', marginLeft: 'auto' }}>
                      {[w.county, w.state].filter((s) => s !== null).join(', ')}
                      {w.tankCount > 0 ? ` · ${plural(w.tankCount, 'tank')}` : ''}
                    </span>
                  </label>
                </li>
              ))}
            </ul>
          )}
          {matches.length > shown.length && (
            <p style={{ ...MUTED, fontSize: '12px' }}>
              Showing {shown.length} of {matches.length}. Search to narrow the list.
            </p>
          )}
        </>
      )}
      <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginTop: '8px' }}>
        <button
          type="button"
          style={PRIMARY_BUTTON}
          disabled={setWells.isPending || wells.data === undefined}
          onClick={(): void => {
            void save();
          }}
        >
          Save wells
        </button>
        {message !== null && (
          <span role={message.ok ? 'status' : 'alert'} style={message.ok ? OK_TEXT : ERROR_TEXT}>
            {message.text}
          </span>
        )}
      </div>
    </section>
  );
}

function batteryLabel(t: OperatorTankView): string {
  const site = t.siteLabel !== null && t.siteLabel.trim() !== '' ? t.siteLabel : 'No battery';
  return `${site} — ${t.name}`;
}

function BatteryTanksEditor({ route }: { readonly route: RouteView }): React.JSX.Element {
  const tanks = useOperatorTanks();
  const setTanks = useSetRouteTanks();
  const [selected, setSelected] = React.useState<ReadonlySet<string>>(
    () => new Set(route.tanks.map((t) => t.tankId)),
  );
  const [message, setMessage] = React.useState<{ ok: boolean; text: string } | null>(null);

  // Battery tanks only (no single well). Inactive ones only while they are still on the route.
  const battery = (tanks.data?.tanks ?? [])
    .filter((t) => t.wellId === null && (t.isActive || selected.has(t.id)))
    .sort((a, b) => batteryLabel(a).localeCompare(batteryLabel(b)));

  const toggle = (id: string): void => {
    setMessage(null);
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const save = async (): Promise<void> => {
    setMessage(null);
    try {
      await setTanks.mutateAsync({ id: route.id, tankIds: [...selected] });
      setMessage({ ok: true, text: 'Battery tanks saved.' });
    } catch (err) {
      setMessage({ ok: false, text: portalErrorText(err, 'Could not save the battery tanks.') });
    }
  };

  return (
    <section aria-label="Battery tanks on this route" style={{ marginTop: '20px' }}>
      <h3 style={{ fontSize: '15px', margin: '0 0 6px' }}>Battery tanks</h3>
      <p style={{ ...MUTED, fontSize: '13px', margin: '0 0 8px' }}>
        Tanks on a battery rather than a single well. Tanks on a well come with the well.
      </p>
      {tanks.isLoading && <p role="status">Loading tanks…</p>}
      {tanks.isError && <PortalDataError error={tanks.error} fallback="Could not load tanks." />}
      {tanks.data !== undefined &&
        (battery.length === 0 ? (
          <p style={{ ...MUTED, fontSize: '13px' }}>You have no battery tanks.</p>
        ) : (
          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {battery.map((t) => (
              <li key={t.id} style={{ padding: '4px 0' }}>
                <label style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                  <input
                    type="checkbox"
                    checked={selected.has(t.id)}
                    onChange={(): void => {
                      toggle(t.id);
                    }}
                  />
                  <span>{batteryLabel(t)}</span>
                  {!t.isActive && <span style={TAG}>Inactive</span>}
                </label>
              </li>
            ))}
          </ul>
        ))}
      <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginTop: '8px' }}>
        <button
          type="button"
          style={PRIMARY_BUTTON}
          disabled={setTanks.isPending || tanks.data === undefined}
          onClick={(): void => {
            void save();
          }}
        >
          Save battery tanks
        </button>
        {message !== null && (
          <span role={message.ok ? 'status' : 'alert'} style={message.ok ? OK_TEXT : ERROR_TEXT}>
            {message.text}
          </span>
        )}
      </div>
    </section>
  );
}

function foremanLabel(f: CompanyForemanView): string {
  return f.name ?? f.email ?? 'Unnamed foreman';
}

function ForemenEditor({
  route,
  viewer,
}: {
  readonly route: RouteView;
  readonly viewer: Viewer;
}): React.JSX.Element {
  const foremen = useCompanyForemen(viewer.isAdmin);
  const setForeman = useSetRouteForeman();
  const [error, setError] = React.useState<string | null>(null);
  const onRoute = new Set(route.foremen.map((f) => f.userId));

  const change = async (userId: string, on: boolean): Promise<void> => {
    setError(null);
    try {
      await setForeman.mutateAsync({ id: route.id, userId, on });
    } catch (err) {
      setError(portalErrorText(err, 'Could not change the foremen on this route.'));
    }
  };

  return (
    <section aria-label="Foremen on this route" style={{ marginTop: '20px' }}>
      <h3 style={{ fontSize: '15px', margin: '0 0 6px' }}>Foremen</h3>
      {viewer.isAdmin ? (
        <>
          {foremen.isLoading && <p role="status">Loading foremen…</p>}
          {foremen.isError && (
            <PortalDataError error={foremen.error} fallback="Could not load foremen." />
          )}
          {foremen.data !== undefined &&
            (foremen.data.foremen.length === 0 ? (
              <p style={{ ...MUTED, fontSize: '13px' }}>
                Your company has no foremen yet. Ask Paelode to add them.
              </p>
            ) : (
              <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                {foremen.data.foremen.map((f) => {
                  const on = onRoute.has(f.userId);
                  const label = foremanLabel(f);
                  return (
                    <li
                      key={f.userId}
                      style={{
                        display: 'flex',
                        gap: '10px',
                        alignItems: 'center',
                        padding: '4px 0',
                      }}
                    >
                      <span style={{ minWidth: '200px' }}>{label}</span>
                      <span style={{ ...MUTED, fontSize: '13px', minWidth: '120px' }}>
                        {on ? 'On this route' : 'Not on this route'}
                      </span>
                      <button
                        type="button"
                        style={BUTTON}
                        disabled={setForeman.isPending}
                        aria-label={on ? `Remove ${label}` : `Add ${label}`}
                        onClick={(): void => {
                          void change(f.userId, !on);
                        }}
                      >
                        {on ? 'Remove' : 'Add'}
                      </button>
                    </li>
                  );
                })}
              </ul>
            ))}
        </>
      ) : (
        <>
          <p style={{ ...MUTED, fontSize: '13px', margin: '0 0 8px' }}>
            {onRoute.has(viewer.userId)
              ? 'This is one of your routes.'
              : 'This is not one of your routes.'}
          </p>
          <button
            type="button"
            style={BUTTON}
            disabled={setForeman.isPending}
            onClick={(): void => {
              void change(viewer.userId, !onRoute.has(viewer.userId));
            }}
          >
            {onRoute.has(viewer.userId) ? 'Remove from my routes' : 'Add to my routes'}
          </button>
        </>
      )}
      {error !== null && (
        <p role="alert" style={{ ...ERROR_TEXT, marginTop: '6px' }}>
          {error}
        </p>
      )}
    </section>
  );
}
